import apiService from './apiService';
import shareLinkService from './shareLinkService';

/**
 * Service pour les commandes
 */
class OrderService {
  /**
   * Crée une commande
   * @param {Object} orderData - Données de la commande (conformes à OrderRequest)
   * {
   *   payment_method: 'Airtel Money' | 'En espèces',
   *   shipping_mode?: 'fret aérien' | 'fret maritime' | 'aucun',
   *   product_type: 'sample' | 'normal',
   *   affiliate_token?: string | null,
   *   product_variants: Array<{ id: string; quantity: number; }>
   * }
   * @returns {Promise<{data: object, payment: object|null}>} La commande, et
   *   l'état du paiement mobile money démarré côté API (`null` en espèces) —
   *   `additional(['payment' => …])` le place à côté de `data`, pas dedans.
   */
  async createOrder(orderData) {
    // Inclure le token d'affiliation s'il existe dans localStorage
    const affiliateToken = shareLinkService.getAffiliateToken();
    const dataWithToken = {
      ...orderData,
      affiliate_token: orderData.affiliate_token ?? affiliateToken ?? null,
    };

    const response = await apiService.post('/orders', dataWithToken);

    // Nettoyer le token après une commande réussie
    if (affiliateToken) {
      shareLinkService.clearAffiliateToken();
    }

    return response;
  }

  /**
   * Récupère la liste des commandes
   * @param {Object} params - filtres éventuels (search, paginate, etc.)
   */
  async getOrders(params = {}) {
    const response = await apiService.get('/orders', params);
    return response.data;
  }

  /**
   * Récupère le détail d'une commande
   * @param {string} id - ID de la commande
   */
  async getOrder(id) {
    const response = await apiService.get(`/orders/${id}`);
    return response.data;
  }

  /**
   * Met à jour le statut d'une commande (admin)
   * @param {string} id - ID de la commande
   * @param {string} status - Nouveau statut ('Payé', 'Livré', 'Annulé')
   */
  async updateOrderStatus(id, status) {
    const response = await apiService.patch(`/orders/${id}/status`, { status });
    return response.data;
  }

  /**
   * Relance le paiement mobile money d'une commande déjà créée (bouton
   * « Payer » du suivi de commande) — ne crée rien, redémarre l'appel
   * passerelle sur la commande existante.
   * @param {string} id - ID de la commande
   * @param {{payment_method: 'Airtel Money'|'Moov Money', mobile_money_phone: string}} paymentData
   * @returns {Promise<{data: object, payment: object}>} Même forme que `createOrder`.
   */
  async payOrder(id, paymentData) {
    const response = await apiService.post(`/orders/${id}/pay`, paymentData);
    return response;
  }

  /**
   * Paie le solde de transport d'une commande en mobile money (bouton
   * « Payer le transport » du suivi de commande) — Airtel/Moov uniquement,
   * jamais le cash : voir `Order::remaining_transport_amount`.
   * @param {string} id - ID de la commande
   * @param {{payment_method: 'Airtel Money'|'Moov Money', mobile_money_phone: string}} paymentData
   * @returns {Promise<{data: object, payment: object}>} Même forme que `payOrder`.
   */
  async payTransport(id, paymentData) {
    const response = await apiService.post(`/orders/${id}/final-transport/pay`, paymentData);
    return response;
  }
}

const orderServiceInstance = new OrderService();
export default orderServiceInstance;
